/**
 * The tiles of a map's virtual rooms (SC-422). Reads go straight to `VirtualRoomTile` by `mapId`
 * or `virtualRoomId`. Writes go through `saveVirtualRoomTiles`, which checks for a platform admin
 * and that every tile lies inside the map.
 */
import Parse from '../initializeParse'
import { parseCloudRun } from '../parseCloud'

export type TileType = 'room' | 'outline'

export interface VirtualRoomTileData {
    id: string
    /** The VR the tile belongs to (`VirtualRoomTile.virtualRoomId`). */
    roomId: string
    mapId: string
    tileX: number
    tileY: number
    tileType: TileType
}

export interface TileSyncResult {
    success: boolean
    syncedCount: number
    errorCount: number
    errors: string[]
}

export interface TileWrite {
    x: number
    y: number
    tileType?: TileType
}

/** Cloud Code takes at most this many tiles (adds plus removes) per call. */
export const MAX_TILES_PER_CALL = 5000

export class VirtualRoomTileService {
    async getTilesByMapId(mapId: string): Promise<VirtualRoomTileData[]> {
        if (!mapId) return []
        const query = new Parse.Query('VirtualRoomTile')
        query.equalTo('mapId', mapId)
        return this.findAll(query)
    }

    async getTilesByRoomId(roomId: string): Promise<VirtualRoomTileData[]> {
        const query = new Parse.Query('VirtualRoomTile')
        query.equalTo('virtualRoomId', roomId)
        return this.findAll(query)
    }

    /**
     * Adds and removes one VR's tiles. Cloud Code skips a tile it already has, so changing a tile's
     * type means removing it in one call and adding it in the next.
     */
    async saveTiles(mapId: string, roomId: string, add: TileWrite[], remove: TileWrite[]): Promise<{ added: number; removed: number }> {
        let added = 0
        let removed = 0
        const writes = [...remove.map((t) => ({ t, op: 'remove' as const })), ...add.map((t) => ({ t, op: 'add' as const }))]
        for (let i = 0; i < writes.length; i += MAX_TILES_PER_CALL) {
            const chunk = writes.slice(i, i + MAX_TILES_PER_CALL)
            const result = await parseCloudRun<{ added: number; removed: number }>('saveVirtualRoomTiles', {
                mapId,
                virtualRoomId: roomId,
                add: chunk.filter((w) => w.op === 'add').map((w) => w.t),
                remove: chunk.filter((w) => w.op === 'remove').map((w) => w.t),
            })
            added += result.added
            removed += result.removed
        }
        return { added, removed }
    }

    private async findAll(query: Parse.Query): Promise<VirtualRoomTileData[]> {
        const rows: VirtualRoomTileData[] = []
        const pageSize = 1000
        query.limit(pageSize)
        query.ascending('objectId')
        for (let skip = 0; ; skip += pageSize) {
            query.skip(skip)
            const page = await query.find()
            rows.push(...page.map((row: any) => this.parseToVirtualRoomTileData(row)))
            if (page.length < pageSize) return rows
        }
    }

    private parseToVirtualRoomTileData(row: any): VirtualRoomTileData {
        return {
            id: row.id,
            roomId: row.get('virtualRoomId'),
            mapId: row.get('mapId'),
            tileX: row.get('tileX'),
            tileY: row.get('tileY'),
            tileType: row.get('tileType') || 'room',
        }
    }
}

export const virtualRoomTileService = new VirtualRoomTileService()
